import React, { useEffect, useMemo, useState } from 'react';
import { Wallet, PieChart, TrendingUp, Factory, PlusCircle, Save, Trash2 } from 'lucide-react';
import { useAppContext } from '../context/AppContext';
import { formatMoney, getStatusText, getStatusTone } from '../utils/helpers';
import EmptyState from './common/EmptyState';
import StatCard from './StatCard';
import Badge from './common/Badge';
import Button from './common/Button';
import DataTable from './DataTable';
import { t } from '../i18n';

const getPercent = (numerator, denominator) => {
  if (!denominator || denominator === 0) return 0;
  return Math.min(100, Math.max(0, (numerator / denominator) * 100));
};

const BudgetOverview = ({ embedded = false, parkId = '' }) => {
  const {
    currentUser,
    parks,
    categories,
    renewableMilestones,
    projectFinancialOverview,
    setProjectBudgetCurrency,
    upsertProjectBudgetAllocation,
    removeProjectBudgetAllocation,
    upsertProjectIncome,
    purchaseOrders,
    preferences,
    updatePreferences
  } = useAppContext();

  const canEdit = ['project_manager', 'super_user'].includes(currentUser?.role || '');
  const preferredParkId = preferences?.projectContext?.parkId || 'all';
  const initialParkId =
    parkId && parks.some((park) => park.id === parkId)
      ? parkId
      : preferredParkId !== 'all' && parks.some((park) => park.id === preferredParkId)
      ? preferredParkId
      : parks[0]?.id || '';
  const [selectedParkId, setSelectedParkId] = useState(initialParkId);
  const [newAllocation, setNewAllocation] = useState({ name: '', category: 'otros', amount: '' });
  const [newIncome, setNewIncome] = useState({ title: '', milestoneId: '', expectedDate: '', amount: '' });

  useEffect(() => {
    if (!parks || parks.length === 0) return;
    if (parkId && parks.some((park) => park.id === parkId)) {
      setSelectedParkId(parkId);
      return;
    }
    if (preferredParkId !== 'all' && parks.some((park) => park.id === preferredParkId)) {
      setSelectedParkId(preferredParkId);
      return;
    }
    if (!selectedParkId) {
      setSelectedParkId(parks[0]?.id || '');
    }
  }, [parks, preferredParkId, parkId]);

  useEffect(() => {
    if (!selectedParkId) return;
    updatePreferences({ projectContext: { parkId: selectedParkId } });
  }, [selectedParkId, updatePreferences]);

  if (!parks || parks.length === 0) {
    return (
      <EmptyState
        title={t('Presupuesto: no hay proyectos para mostrar')}
        description={t(
          'Para visualizar y controlar presupuesto, primero debes crear al menos un proyecto.'
        )}
        checklist={[
          'Crear un proyecto en Administración o Desarrollo.',
          'Asignar Project Manager (PM).',
          'Definir partidas (allocations) del presupuesto.'
        ]}
        primaryAction={{ label: 'Ir a Administración', pageId: 'admin' }}
        secondaryAction={{ label: 'Ir a Desarrollo', pageId: 'development-portfolio' }}
      />
    );
  }

  const summary = useMemo(() => {
    if (!projectFinancialOverview || projectFinancialOverview.length === 0) {
      return {
        totalBudget: 0,
        totalCommitted: 0,
        totalInvoiced: 0,
        totalAvailable: 0,
        totalIncomePlanned: 0,
        totalIncomeReceived: 0
      };
    }

    return projectFinancialOverview.reduce(
      (acc, park) => ({
        totalBudget: acc.totalBudget + (park.totalBudget || 0),
        totalCommitted: acc.totalCommitted + (park.totalCommitted || 0),
        totalInvoiced: acc.totalInvoiced + (park.totalInvoiced || 0),
        totalAvailable: acc.totalAvailable + (park.available || 0),
        totalIncomePlanned: acc.totalIncomePlanned + (park.totalIncomePlanned || 0),
        totalIncomeReceived: acc.totalIncomeReceived + (park.totalIncomeReceived || 0)
      }),
      {
        totalBudget: 0,
        totalCommitted: 0,
        totalInvoiced: 0,
        totalAvailable: 0,
        totalIncomePlanned: 0,
        totalIncomeReceived: 0
      }
    );
  }, [projectFinancialOverview]);

  const selectedProjectFinancial = useMemo(() => {
    return projectFinancialOverview.find((item) => item.parkId === selectedParkId) || null;
  }, [projectFinancialOverview, selectedParkId]);
  const selectedCurrency = selectedProjectFinancial?.currency || 'USD';

  const categoryOptions = useMemo(() => {
    const map = new Map();
    categories.forEach((category) => {
      map.set(category.budgetCategory || 'otros', category.budgetCategory || 'otros');
    });
    const options = Array.from(map.values());
    return options.length > 0 ? options : ['otros'];
  }, [categories]);

  const selectedParkMilestones = useMemo(() => {
    const projectByPark = {
      'park-leyda': 'ren-leyda',
      'park-hurtado': 'ren-hurtado',
      'park-frutillas': 'ren-frutillas'
    };
    const projectId = projectByPark[selectedParkId] || null;
    if (!projectId) return [];
    return renewableMilestones.filter((item) => item.projectId === projectId);
  }, [renewableMilestones, selectedParkId]);

  const linkedOrders = useMemo(() => {
    return purchaseOrders.filter((order) => order.parkId === selectedParkId && order.status !== 'rechazada');
  }, [purchaseOrders, selectedParkId]);

  const linkedOrdersColumns = useMemo(() => [
    { key: 'id', label: 'OC', sortable: true },
    {
      key: 'status',
      label: t('Estado'),
      sortable: true,
      render: (status) => <Badge tone={getStatusTone(status)}>{t(getStatusText(status))}</Badge>
    },
    {
      key: 'categoryName',
      label: t('Categoria'),
      render: (value, row) => value || row.categoryId || 'otros'
    },
    {
      key: 'totalValue',
      label: t('Total'),
      align: 'right',
      sortable: true,
      render: (value) => formatMoney(value || 0, selectedCurrency)
    },
    {
      key: 'invoicedValue',
      label: t('Facturado'),
      align: 'right',
      sortable: true,
      render: (value) => formatMoney(value || 0, selectedCurrency)
    }
  ], [selectedCurrency]);

  const handleSaveAllocation = (allocation) => {
    const nextAmount = window.prompt(t('Ingresa nuevo monto para la partida'), String(allocation.amount || 0));
    if (nextAmount == null) return;
    upsertProjectBudgetAllocation(selectedParkId, {
      id: allocation.id,
      name: allocation.name,
      category: allocation.category,
      amount: Number(nextAmount || 0),
      currency: selectedCurrency
    });
  };

  const handleCreateAllocation = () => {
    if (!newAllocation.name || !newAllocation.amount) return;
    upsertProjectBudgetAllocation(selectedParkId, {
      name: newAllocation.name,
      category: newAllocation.category,
      amount: Number(newAllocation.amount),
      currency: selectedCurrency
    });
    setNewAllocation({ name: '', category: 'otros', amount: '' });
  };

  const handleCreateIncome = () => {
    if (!newIncome.title || !newIncome.amount) return;
    upsertProjectIncome({
      parkId: selectedParkId,
      title: newIncome.title,
      milestoneId: newIncome.milestoneId || null,
      expectedDate: newIncome.expectedDate || null,
      amount: Number(newIncome.amount),
      status: 'pendiente',
      receivedAmount: 0
    });
    setNewIncome({ title: '', milestoneId: '', expectedDate: '', amount: '' });
  };

  const markIncomeReceived = (income) => {
    upsertProjectIncome({
      ...income,
      status: 'recibido',
      receivedAmount: Number(income.amount || 0)
    });
  };

  return (
    <div className="space-y-6">
      {!embedded && (
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">{t('Presupuestos de Proyectos')}</h1>
            <p className="text-sm text-slate-500">{t(
              'Gastos de OC vinculados por proyecto/categoria, y gestion de ingresos por hito.'
            )}</p>
          </div>
          <select
            value={selectedParkId}
            onChange={(event) => setSelectedParkId(event.target.value)}
            className="px-4 py-2 border border-slate-200 rounded-xl text-sm"
          >
            {parks.map((park) => (
              <option key={park.id} value={park.id}>
                {t(park.name)}
              </option>
            ))}
          </select>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        <StatCard
          title={t('Presupuesto Total')}
          value={formatMoney(selectedProjectFinancial?.totalBudget ?? summary.totalBudget, selectedCurrency)}
          icon={Wallet}
        />
        <StatCard
          title={t('Comprometido (OC)')}
          value={formatMoney(selectedProjectFinancial?.totalCommitted ?? summary.totalCommitted, selectedCurrency)}
          icon={TrendingUp}
        />
        <StatCard
          title={t('Facturado')}
          value={formatMoney(selectedProjectFinancial?.totalInvoiced ?? summary.totalInvoiced, selectedCurrency)}
          icon={PieChart}
        />
        <StatCard
          title={t('Disponible')}
          value={formatMoney(selectedProjectFinancial?.available ?? summary.totalAvailable, selectedCurrency)}
          icon={Factory}
        />
        <StatCard
          title={t('Ingresos planificados')}
          value={formatMoney(selectedProjectFinancial?.totalIncomePlanned ?? summary.totalIncomePlanned, selectedCurrency)}
          icon={Wallet}
        />
        <StatCard
          title={t('Ingresos recibidos')}
          value={formatMoney(selectedProjectFinancial?.totalIncomeReceived ?? summary.totalIncomeReceived, selectedCurrency)}
          icon={Wallet}
        />
      </div>

      {selectedProjectFinancial && (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
          <section className="bg-white border border-slate-200 rounded-3xl p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-slate-900">{t('Partidas de presupuesto')}</h2>
              {canEdit ? <span className="text-xs text-emerald-700">{t('Editable por')} {t(currentUser.role)}</span> : <span className="text-xs text-slate-500">{t('Solo lectura')}</span>}
            </div>

            <div className="mb-4 flex items-center justify-between gap-3 rounded-2xl border border-slate-200 bg-slate-50 p-4">
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wide">{t('Moneda del presupuesto')}</p>
                <p className="text-sm font-semibold text-slate-900">
                  {selectedCurrency === 'CLP' ? t('CLP (Pesos chilenos)') : t('USD (Dólar americano)')}
                </p>
                <p className="text-[11px] text-slate-500">{t('Por defecto es USD. Cambia a CLP solo si el control se hace en pesos.')}</p>
              </div>
              <select
                value={selectedCurrency}
                onChange={(event) => setProjectBudgetCurrency(selectedParkId, event.target.value)}
                disabled={!canEdit}
                className="px-3 py-2 rounded-xl border border-slate-200 text-sm bg-white disabled:opacity-60"
              >
                <option value="USD">{t('USD')}</option>
                <option value="CLP">{t('CLP')}</option>
              </select>
            </div>

            <div className="space-y-3">
              {selectedProjectFinancial.allocations.map((allocation) => {
                const committedPct = getPercent(allocation.committed, allocation.amount);
                const invoicedPct = getPercent(allocation.invoiced, allocation.amount);
                return (
                  <div key={allocation.id} className="border border-slate-100 rounded-2xl p-4 bg-slate-50">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <p className="font-semibold text-slate-800">{t(allocation.name)}</p>
                        <p className="text-xs text-slate-500">{t('Categoria:')} {t(allocation.category)}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-slate-500">{t('Plan')}</p>
                        <p className="font-semibold text-slate-800">{formatMoney(allocation.amount, selectedCurrency)}</p>
                      </div>
                    </div>

                    <div className="mt-3 space-y-2 text-xs text-slate-600">
                      <p>{t('Comprometido por OC:')} {formatMoney(allocation.committed, selectedCurrency)}</p>
                      <div className="w-full h-2 bg-white rounded-full overflow-hidden"><div className="h-full bg-warning-500" style={{ width: `${committedPct}%` }} /></div>
                      <p>{t('Facturado:')} {formatMoney(allocation.invoiced, selectedCurrency)}</p>
                      <div className="w-full h-2 bg-white rounded-full overflow-hidden"><div className="h-full bg-success-500" style={{ width: `${invoicedPct}%` }} /></div>
                      <p>{t('Disponible:')} {formatMoney(allocation.available, selectedCurrency)}</p>
                    </div>

                    {canEdit && (
                      <div className="mt-3 flex gap-2">
                        <Button size="sm" variant="secondary" icon={Save} onClick={() => handleSaveAllocation(allocation)}>{t('Editar monto')}</Button>
                        <Button size="sm" variant="danger" icon={Trash2} onClick={() => removeProjectBudgetAllocation(selectedParkId, allocation.id)}>{t('Eliminar')}</Button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {canEdit && (
              <div className="mt-4 border-t border-slate-200 pt-4 grid grid-cols-1 md:grid-cols-4 gap-2">
                <input value={newAllocation.name} onChange={(e) => setNewAllocation((p) => ({ ...p, name: e.target.value }))} placeholder={t('Nueva partida')} className="px-3 py-2 rounded-lg border border-slate-200 text-sm" />
                <select value={newAllocation.category} onChange={(e) => setNewAllocation((p) => ({ ...p, category: e.target.value }))} className="px-3 py-2 rounded-lg border border-slate-200 text-sm">
                  {categoryOptions.map((option) => <option key={option}>{t(option)}</option>)}
                </select>
                <input type="number" value={newAllocation.amount} onChange={(e) => setNewAllocation((p) => ({ ...p, amount: e.target.value }))} placeholder={t('Monto')} className="px-3 py-2 rounded-lg border border-slate-200 text-sm" />
                <Button variant="primary" icon={PlusCircle} onClick={handleCreateAllocation}>{t('Agregar')}</Button>
              </div>
            )}
          </section>

          <section className="bg-white border border-slate-200 rounded-3xl p-6">
            <h2 className="text-xl font-semibold text-slate-900 mb-2">{t('Ingresos por hito')}</h2>
            <p className="text-xs text-slate-500 mb-4">{t('Estos ingresos impactan margen proyectado y margen real del proyecto.')}</p>

            <div className="space-y-3">
              {selectedProjectFinancial.incomes.map((income) => (
                <div key={income.id} className="border border-slate-100 rounded-2xl p-4 bg-slate-50">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-semibold text-slate-800">{t(income.title)}</p>
                      <p className="text-xs text-slate-500">{t('Fecha esperada:')} {t(income.expectedDate) || '—'}</p>
                    </div>
                    <Badge tone={income.status === 'recibido' ? 'success' : 'warning'}>{t(income.status)}</Badge>
                  </div>
                  <p className="text-sm text-slate-700 mt-2">{t('Plan:')} {formatMoney(income.amount, selectedCurrency)} {t('· Recibido:')} {formatMoney(income.receivedAmount || 0, selectedCurrency)}
                  </p>
                  {canEdit && income.status !== 'recibido' ? (
                    <Button size="sm" variant="secondary" className="mt-2" onClick={() => markIncomeReceived(income)}>{t('Marcar recibido')}</Button>
                  ) : null}
                </div>
              ))}
            </div>

            {canEdit && (
              <div className="mt-4 border-t border-slate-200 pt-4 grid grid-cols-1 md:grid-cols-2 gap-2">
                <input value={newIncome.title} onChange={(e) => setNewIncome((p) => ({ ...p, title: e.target.value }))} placeholder={t('Nombre del hito')} className="px-3 py-2 rounded-lg border border-slate-200 text-sm" />
                <select value={newIncome.milestoneId} onChange={(e) => setNewIncome((p) => ({ ...p, milestoneId: e.target.value }))} className="px-3 py-2 rounded-lg border border-slate-200 text-sm">
                  <option value="">{t('Sin vinculo de hito')}</option>
                  {selectedParkMilestones.map((milestone) => (
                    <option key={milestone.id} value={milestone.id}>{t(milestone.title)}</option>
                  ))}
                </select>
                <input type="date" value={newIncome.expectedDate} onChange={(e) => setNewIncome((p) => ({ ...p, expectedDate: e.target.value }))} className="px-3 py-2 rounded-lg border border-slate-200 text-sm" />
                <input type="number" value={newIncome.amount} onChange={(e) => setNewIncome((p) => ({ ...p, amount: e.target.value }))} placeholder={t('Monto ingreso')} className="px-3 py-2 rounded-lg border border-slate-200 text-sm" />
                <Button variant="primary" icon={PlusCircle} className="md:col-span-2" onClick={handleCreateIncome}>{t('Agregar ingreso por hito')}</Button>
              </div>
            )}

            <div className="mt-4 rounded-xl border border-slate-200 p-3 bg-slate-50 text-sm">
              <p><span className="font-semibold">{t('Margen forecast:')}</span> {formatMoney(selectedProjectFinancial.marginForecast || 0, selectedCurrency)}</p>
              <p><span className="font-semibold">{t('Margen real:')}</span> {formatMoney(selectedProjectFinancial.marginReal || 0, selectedCurrency)}</p>
            </div>
          </section>
        </div>
      )}

      <section className="bg-white border border-slate-200 rounded-3xl p-6">
        <h2 className="text-xl font-semibold text-slate-900 mb-2">{t('OC vinculadas al proyecto')}</h2>
        <p className="text-xs text-slate-500 mb-4">{t(
          'Las OC impactan automáticamente el comprometido/facturado de cada partida.'
        )}</p>
        <DataTable
          data={linkedOrders}
          columns={linkedOrdersColumns}
          searchPlaceholder={t('Buscar OC...')}
          emptyTitle={t('No hay OC vinculadas a este proyecto.')}
          emptyDescription={t('Las órdenes de compra generadas para este proyecto aparecerán aquí.')}
        />
      </section>
    </div>
  );
};

export default BudgetOverview;
